import { useEffect, useState } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY } from "@/data/content";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Enquiry", to: "/enquiry" },
  { label: "Machines", to: "/machines" },
  { label: "Clients", to: "/clients" },
  { label: "Contact", to: "/contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-white/95 backdrop-blur-md shadow-sm py-2.5" : "py-4 bg-white/70 backdrop-blur-sm"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 min-w-0" data-testid="nav-logo">
            <img src={COMPANY.logo} alt="Accurate Logo" className="h-9 md:h-10 w-auto rounded-md shrink-0" data-testid="nav-brand-logo" />
            <span className="font-display text-lg md:text-xl tracking-tight text-slate-900 leading-none whitespace-nowrap truncate">
              Accurate <span className="text-[#0055FF]">Automation &amp; Reconditioners</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to}
                className={({ isActive }) => `nav-link text-[13px] font-medium transition-colors ${isActive ? "text-slate-900 active" : "text-slate-700 hover:text-slate-900"}`}
                data-testid={`nav-${item.label.toLowerCase()}`}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/quote" className="hidden md:inline-flex btn-primary !py-2 !px-4 !text-xs" data-testid="nav-quote-btn">Request a Quote</Link>
            <button className="lg:hidden text-slate-900 p-2" onClick={() => setOpen((v) => !v)} data-testid="nav-mobile-toggle" aria-label="Menu">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
            className="fixed inset-0 z-40 lg:hidden bg-white pt-24 px-8 overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 pb-10">
              {NAV.map((item) => (
                <Link key={item.to} to={item.to}
                  className="py-3 text-base border-b border-slate-200 text-slate-800 hover:text-[#0055FF] transition-colors"
                  data-testid={`mnav-${item.to}`}>
                  {item.label}
                </Link>
              ))}
              <a href={`tel:${COMPANY.phone}`} className="btn-primary mt-6 justify-center">
                <Phone className="w-4 h-4" /> {COMPANY.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
